<template>
    <v-container>
        <v-row>
            <!-- CALENDAR -->
            <v-slide-y-transition>
                <v-col cols="12" md="6" v-if="showCalendar || !isMobile">
                    <v-date-picker v-model="calendar" :events="daysWithRoutines" color="#CCFF99"
                        event-color="grey darken-1" locale="es-CL" first-day-of-week="1" full-width no-title multiple
                        :show-current="false" :picker-date.sync="pickerDate" />
                    <v-divider v-if="isMobile" class="divider-primary" />
                </v-col>
            </v-slide-y-transition>

            <v-col cols="12" md="6">
                <loading-spinner v-if="loading" />
                <v-row v-else>
                    <!-- ROUTINES -->
                    <v-col cols="12" v-if="hasCoach">
                        <v-expansion-panels focusable accordion flat class="kimsa-elevation" v-model="openFirst">
                            <v-expansion-panel v-for="(item, i) in filterRoutines" :key="i">
                                <v-expansion-panel-header :class="['title-date', { 'title-date-separator': i !== 0 }]"
                                    ripple>
                                    {{ item.dateString }}
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <v-sheet height="28" color="transparent" />
                                    <v-row>
                                        <v-col v-for="(routine, j) in item.routines" :key="j" cols="12" class="mb-1">
                                            <competence-card v-if="routine.type === 'competence'" :competence="routine"
                                                @click="goToCompetenceDetail(routine)" />
                                            <routine-card v-else :routine="routine"
                                                @click="showRoutineDetail(routine)" />
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-col>

                    <!-- NO HIRED COACH -->
                    <v-col cols="12" v-else>
                        <v-img eager :src="require('@/assets/img/card_placeholder.png')" />
                        <div class="subtext">
                            {{ $t('coach.no_hired_coach') }}
                        </div>
                        <v-btn color="b-primary" class="normal-btn" depressed block large @click="toCoaches">
                            {{ $t('coach.search_coach') }}
                        </v-btn>
                    </v-col>

                    <!-- NO ROUTINES -->
                    <v-col cols="12" v-if="routinesStudent.length === 0">
                        <v-img eager :src="require('@/assets/img/card_placeholder.png')" />
                        <div class="subtext">
                            {{ $t('coach.no_assigned_routines') }}
                        </div>
                    </v-col>

                    <!-- NO ROUTINES TODAY -->
                    <v-col cols="12" v-else-if="filterRoutines.length === 0">
                        <div class="subtext">
                            {{ $t('coach.no_assigned_routines_day') }}
                        </div>
                    </v-col>

                    <!-- ROUTINE DETAIL DIALOG -->
                    <routine-detail-dialog ref="routineDetail" />

                    <fab-menu :items="options" fixed-bottom @click:injury="handleInjury"
                        @click:activity="handleActivity" @click:competence="handleCompetence" />

                    <injury-form-dialog ref="injury" />
                    <add-activity-dialog ref="activity" />
                    <add-competence-dialog :current-selection="currentSelection" collection="assignedRoutines"
                        :coach-id="coachId" ref="competence" @saved="onSaveCompetence" select-date />

                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

import { mapActions, mapMutations, mapState } from 'vuex'

import RoutineCard from '@/components/common/routines/RoutineCard'
import RoutineDetailDialog from '@/components/student/RoutineDetailDialog'
import LoadingSpinner from "@/components/common/LoadingSpinner";
import CompetenceCard from '../../components/common/routines/CompetenceCard.vue';
import FabMenu from '../../components/common/FabMenu.vue';
import InjuryFormDialog from '../../components/student/InjuryFormDialog.vue';
import AddActivityDialog from '../../components/student/AddActivityDialog.vue';
import AddCompetenceDialog from '../../components/coach/competences/AddCompetenceDialog.vue';
import { db, Timestamp } from "@/utils/Firebase";

export default {
    components: {
        LoadingSpinner,
        RoutineCard,
        RoutineDetailDialog,
        CompetenceCard,
        FabMenu,
        InjuryFormDialog,
        AddActivityDialog,
        AddCompetenceDialog,
    },
    data() {
        return {
            loading: false,
            openFirst: 0,
            calendar: [],
            dates: [],
            pickerDate: null,
            routines: [],
            options: [
                { icon: 'mdi-soccer-field', value: 'competence' },
                { icon: 'mdi-calendar-plus', value: 'activity' },
                { icon: 'mdi-account-injury', value: 'injury' },
            ]
        }
    },
    computed: {
        ...mapState(['showCalendar', 'routinesStudent']),
        hasCoach(vm = this) {
            return !!vm.account.studentSubscription
        },
        filterRoutines(vm = this) {
            if (vm.calendar.length === 0) return vm.routines
            return vm.routines.filter(d => vm.calendar.includes(d.date))
        },
        queryDate(vm = this) {
            return vm.$route.query.date || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        currentSelection(vm = this) {
            return { ...vm.account, objectID: vm.currentUser.uid }
        }
    },
    async mounted() {
        const vm = this
        vm.pickerDate = vm.queryDate
        let date = vm.$moment().format('YYYY-MM-DD')
        await vm.loadRoutines(date)
    },
    methods: {
        ...mapMutations(['setShowCalendar', 'setTitle']),
        ...mapActions(['fetchRoutinesStudent']),
        async loadRoutines(date) {
            const vm = this
            vm.loading = true
            await vm.fetchRoutinesStudent({ studentId: vm.currentUser.uid, date: date })
            vm.loading = false
        },
        toCoaches() {
            const vm = this
            vm.$router.push({ name: 'student.coaches' })
        },
        daysWithRoutines(date) {
            const vm = this
            let result = []
            vm.dates.forEach(d => {
                if (d.includes(date)) result.push('grey darken-1')
            })
            return result
        },
        showRoutineDetail(routine) {
            const vm = this
            vm.$refs.routineDetail.setRoutine(routine)
            vm.$refs.routineDetail.show()
        },
        groupByDate(array) {
            return array.reduce((r, c) => {
                (r[c.rawDate.full] = r[c.rawDate.full] || []).push(c)
                return r
            }, {})
        },
        async makeRoutines() {
            const vm = this
            vm.loading = true
            vm.routines = []
            vm.dates = []
            let groupRoutines = vm.groupByDate(vm.routinesStudent)
            let routinesMapped = new Map(Object.entries(groupRoutines))
            routinesMapped.forEach(routines => {
                let date = routines[0].rawDate.full
                vm.routines.push({
                    dateString: vm.$moment(date).format('D [de] MMMM'),
                    date: date,
                    routines: routines
                })
                vm.dates.push(date)
            })
            vm.loading = false
        },
        goToCompetenceDetail(competence) {
            const vm = this;
            vm.$router.push({ name: 'student.routine.competence.detail', params: { uid: competence.id } })
        },
        handleInjury() {
            const vm = this;
            vm.$refs.injury.show()
        },
        handleActivity() {
            const vm = this;
            vm.$refs.activity.show()
        },
        handleCompetence() {
            const vm = this;
            vm.$refs.competence.show()
        },
        async onSaveCompetence(payload) {
            const vm = this;
            let date = vm.$moment(payload.date).format('YYYY-MM-DD')
            await vm.loadRoutines(date)
            vm.sendMessageToCoach(payload)
        },
        async sendMessageToCoach(competence) {
            const vm = this

            let message = `${vm.$t('common.recorded_competence')} \n${vm.$t('common.date')}: ${vm.$moment(competence.date).format('DD-MM-YYYY')} \n${vm.$t('common.versus_short')}: ${competence.versus}`

            let studentId = vm.currentUser?.uid || null
            let coachId = vm.coachId

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[coachId, studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [coachId, studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: message,
                from: studentId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'text'
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({ lastMessage: payload })
        },
    },
    watch: {
        routinesStudent() {
            this.makeRoutines()
        },
        async pickerDate(val) {
            let vm = this
            let date = vm.$moment(val).format('YYYY-MM-DD')
            vm.loadRoutines(date)
        },
        calendar() {
            this.openFirst = 0
        }
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        vm.setShowCalendar(false)
        if (to.name !== 'student.routine.detail.exercise') vm.setTitle('')
        next()
    }
}
</script>

<style scoped>
.subtext {
    font-size: 15px;
    color: var(--v-secondary-base);
    text-align: center;
    margin-bottom: 8px;
}

.divider-primary {
    height: 3px;
    max-height: 10px;
    background-color: var(--v-primary-base);
    margin-top: 10px;
    margin-bottom: 10px;
}

.title-date {
    color: var(--v-accent-base);
    font-weight: bold;
    border-radius: 4px;
}

.title-date-separator {
    border-top: 1px solid rgba(51, 51, 51, 0.1);
}

::v-deep .v-date-picker-table {
    height: 100%;
}

::v-deep .v-date-picker-table .v-btn.v-btn--active {
    color: #999;
    box-shadow: 3px 3px 6px 0 rgba(1, 55, 46, 0.1) !important;
}

::v-deep .v-date-picker-table .v-btn.v-btn--rounded {
    border-radius: 5px;
}

::v-deep .v-date-picker-table__events {
    height: 10px;
}

::v-deep .v-date-picker-table__events>.grey {
    height: 5px;
    width: 5px;
}
</style>
